import {
    ApiContentSection,
    ApiShopperLandingPageLink,
    ApiSectionType,
} from '@storefronts/common-types'
import { useContext, useEffect, useMemo } from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import {
    PageContext,
    type ActiveLPLinkOptions,
} from '../../../context/PageContextProvider'
import { shouldDeprioritizeCardImage } from '../../../utils/getFirstLcpImageSectionIndex'
import { normalizeLinkHashes } from '../../../utils/resolveLandingPageLinksByHash'
import { ProductCard } from '../../shared/ProductCard'
import { SectionContainer, SectionTitle } from './Shared'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    SECONDARY_CTA_FILL_COLOR_VAR,
    SECONDARY_CTA_TEXT_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { ScrollingLayout } from '../../../style/scrollingLayout'

type ProductCardSectionProps = Extract<
    ApiContentSection,
    { type: ApiSectionType.PRODUCT_CARD }
>

export const ProductCardSection: React.FC<{
    section: ProductCardSectionProps
    isBelowFold?: boolean
}> = ({ section, isBelowFold = true }) => {
    const { setActiveLPLink } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { fetchAndCacheLinksForHashes, getCachedOrderedLinksForHashes } =
        useContext(LandingPageLinksContext)

    const hashes = useMemo(
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        () => normalizeLinkHashes(section.linkHashes),
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        [section.linkHashes],
    )

    const { orderedLinks, allHashesResolved } = useMemo(
        () => getCachedOrderedLinksForHashes(hashes),
        [getCachedOrderedLinksForHashes, hashes],
    )

    useEffect(() => {
        if (allHashesResolved) return
        void fetchAndCacheLinksForHashes(hashes)
    }, [allHashesResolved, hashes, fetchAndCacheLinksForHashes])

    const onProductCardClick = (
        lpLink: ApiShopperLandingPageLink,
        ctx: ActiveLPLinkOptions,
    ) => {
        Analytics.TRACK_LANDING_PAGE_PRODUCT_CARD_CLICK('default', lpLink)
        setActiveLPLink(lpLink, ctx)
    }

    return (
        <SectionContainer maxContentWidth={ScrollingLayout.PROSE_MAX_WIDTH}>
            {section.sectionTitle && (
                <SectionTitle>{section.sectionTitle}</SectionTitle>
            )}
            <div style={{ paddingTop: 8 }}>
                {orderedLinks.map((lpLink, cardIndex) => (
                    <ProductCard
                        key={lpLink.linkHash}
                        background={`var(${PRIMARY_PAGE_FILL_COLOR_VAR})`}
                        buttonColor={`var(${SECONDARY_CTA_FILL_COLOR_VAR})`}
                        buttonTextColor={`var(${SECONDARY_CTA_TEXT_COLOR_VAR})`}
                        lpLink={lpLink}
                        onClick={(_e, ctx) => onProductCardClick(lpLink, ctx)}
                        showAddToCart
                        deprioritizeImage={shouldDeprioritizeCardImage({
                            isBelowFold,
                            cardIndex,
                            firstVisibleCount: 1,
                        })}
                    />
                ))}
            </div>
        </SectionContainer>
    )
}
